'use client';

import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Spinner } from '@/app/components/Spinner';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { RadioButton } from '@/app/components/buttons/RadioButton';
import { Search } from '@/app/components/form/Search';
import { DRAFT_LOCK_QUERY_KEY } from '@/consts/const';
import { CLIENTS_CREATE_PATH } from '@/consts/paths';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import { useBlockClientDraft } from '../../hooks/useBlockClientDraft';
import { useDeleteClient } from '../../hooks/useDeleteClient';
import { useGetClientDrafts } from '../../hooks/useGetClientDrafts';
import ArrowBackIcon from '/public/icons/arrow-back.svg';
import FileIcon from '/public/icons/file-2.svg';
import LockIcon from '/public/icons/lock.svg';
import TrashIcon from '/public/icons/trash.svg';

interface SelectDraftModalProps {
  onGoBack: () => void;
}

export const SelectDraftModal = ({ onGoBack }: SelectDraftModalProps) => {
  const {
    data: draftsResponse,
    isLoading: isDraftsLoading,
    isFetchingNextPage: isFetchingNextDraftPage,
    fetchNextPage: fetchNextDraftPage,
    hasNextPage,
  } = useGetClientDrafts();

  const router = useRouter();
  const [urlSearchQuery, updateSearchQuery] = useSearchQuery();
  const [selectedDraftId, setSelectedDraftId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState(urlSearchQuery.get('search') || '');
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const { mutate: deleteClient, isPending: isDeletingDraft } = useDeleteClient();
  const { mutateAsync: blockClientDraft, isPending: isBlockingDraft } = useBlockClientDraft();

  const handleContinue = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!selectedDraftId) return;
    await blockClientDraft(selectedDraftId, {
      onSuccess: () => {
        router.push(`${CLIENTS_CREATE_PATH}?client_id=${selectedDraftId}&${DRAFT_LOCK_QUERY_KEY}=1`);
      },
      onError: () => {
        toast.error('This draft is currently being edited by another user. Please try again later.');
      },
    });
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    updateSearchQuery({ search: e.target.value });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextDraftPage) {
          void fetchNextDraftPage();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current = observer;
    const currentLoadMoreRef = loadMoreRef.current;

    if (currentLoadMoreRef) {
      observer.observe(currentLoadMoreRef);
    }

    return () => {
      if (currentLoadMoreRef && observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current.unobserve(currentLoadMoreRef);
      }
    };
  }, [fetchNextDraftPage, hasNextPage, isFetchingNextDraftPage]);

  const formatLastEdited = (updatedAt: string) => {
    if (!updatedAt) {
      return 'Last edited date unavailable';
    }
    try {
      const date = new Date(updatedAt);
      return `Last edited on ${date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })} - ${date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })}`;
    } catch {
      return 'Last edited date unavailable';
    }
  };

  return (
    <div className="w-[calc(100vw-2rem)] max-w-[550px] py-8 flex flex-col items-center gap-[36px] md:w-[550px] md:py-[46px]">
      <div className="w-full px-4 md:px-9 flex flex-col items-center gap-[6px]">
        <h2 className="light text-blue-900 text-center max-md:text-lg max-md:leading-[22px] max-md:tracking-[0.006em]">
          Select a draft
        </h2>
        <p className="text-[16px] leading-[24px] text-grey-700 text-center font-inter max-md:text-sm max-md:leading-[150%]">
          Use a saved draft to continue where you left off
        </p>
      </div>

      <div className="w-full flex flex-col items-center gap-4 relative">
        <Search
          name="search"
          placeholder="Search draft"
          value={searchQuery}
          onChange={onSearchChange}
          className="w-full px-4 md:px-9"
        />

        <div className="w-full h-[308px] flex flex-col gap-[2px] overflow-y-auto">
          <>
            {isDraftsLoading ? (
              <Spinner />
            ) : draftsResponse.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <span className="font-inter text-[16px] leading-[22.4px] text-grey-700 max-md:text-sm max-md:leading-[150%]">
                  No drafts found
                </span>
              </div>
            ) : (
              draftsResponse.map((draft) => {
                const isSelected = selectedDraftId === draft.id;
                const isDraftLocked = false; // TODO: Implement lock logic if needed

                return (
                  <div
                    key={draft.id}
                    onClick={() => !isDraftLocked && setSelectedDraftId(draft.id)}
                    className={clsx(
                      'w-full px-4 md:px-9 py-2 flex items-center gap-6',
                      !isDraftLocked && 'hover:bg-grey-100 cursor-pointer transition-colors',
                      isDraftLocked && 'opacity-40 cursor-not-allowed'
                    )}
                  >
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <div className="flex-1 min-w-0 flex items-center gap-3">
                        <div className="w-[34px] h-[34px] flex items-center justify-center">
                          {isDraftLocked ? (
                            <LockIcon className="size-8 max-md:size-7 text-black" />
                          ) : (
                            <FileIcon className="size-8 max-md:size-7 text-blue-300 stroke-blue-300" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col items-start gap-[2px]">
                          <span className="w-full font-inter text-[16px] font-medium leading-[22.4px] text-black text-left break-words line-clamp-2 max-md:text-sm max-md:leading-[20px]">
                            {draft.draft_selection_identifier}
                          </span>
                          <span className="font-inter text-[14px] leading-[19.6px] text-grey-700 text-left max-md:text-xs max-md:leading-[16px]">
                            {formatLastEdited(draft.updated_at)}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteClient(draft.id);
                        }}
                        disabled={isDeletingDraft}
                        className="w-[34px] h-[34px] flex items-center justify-center hover:bg-grey-200 rounded transition-colors"
                      >
                        <TrashIcon className="size-[22px] max-md:size-5 text-red-500" />
                      </button>
                    </div>
                    <RadioButton checked={isSelected} readOnly disabled={isDraftLocked} />
                  </div>
                );
              })
            )}
            {hasNextPage && (
              <div ref={loadMoreRef} className="h-6 flex items-center justify-center mt-[20px]">
                {isFetchingNextDraftPage && <Spinner />}
              </div>
            )}
          </>
        </div>
      </div>

      <div className="w-full px-4 md:px-9 flex items-center gap-5">
        <BaseButton intent="secondary" onClick={onGoBack} size="full" startIcon={<ArrowBackIcon className="w-4 h-4" />}>
          Go Back
        </BaseButton>
        <BaseButton
          intent="primary"
          onClick={handleContinue}
          size="full"
          disabled={!selectedDraftId || isBlockingDraft}
        >
          {isBlockingDraft ? 'Continuing...' : 'Continue'}
        </BaseButton>
      </div>
    </div>
  );
};
